import { ActivatedRoute, Router } from '@angular/router';
import { IView } from '.';
import { Directive, inject, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { IBasePresenter } from './base-presenter';

/**
 * Clase abstracta que representa una vista
 * @template T - Tipo de presentador que implementa la interfaz IBasePresenter
 * @implements {IView} - Interfaz que representa una vista
 * @implements {OnInit} - Interfaz que se ejecuta cuando el componente es inicializado
 * @implements {OnDestroy} - Interfaz que se ejecuta cuando el componente es destruido
 * @implements {AfterViewInit} - Interfaz que se ejecuta cuando la vista ha sido inicializada
 * @implements {OnChanges} - Interfaz que se ejecuta cuando el componente ha cambiado
 */
@Directive()
export abstract class IAbstractView<T extends IBasePresenter> implements IView, OnInit, AfterViewInit, OnDestroy, OnChanges {
  router = inject(Router);
  route = inject(ActivatedRoute);
  cdr = inject(ChangeDetectorRef);

  stepId?: number | undefined;

  /**
   * Constructor del presentador
   * @param presenter - Presentador del componente
   */
  constructor(
    public presenter: T,
  ) {}

  /**
   * Método que se ejecuta cuando el componente es inicializado
   * @returns void
   */
  ngOnInit(): void {
    this.presenter.setView(this, this.cdr);
  }

  /**
   * Método que se ejecuta cuando el componente es destruido
   * @returns void
   */
  ngOnDestroy(): void {
    this.presenter.onDestroy();
  }

  /**
   * Método que se ejecuta cuando la vista ha sido inicializada
   * @returns void
   */
  ngAfterViewInit(): void {
    this.presenter.onAfterViewInit?.();
  }

  /**
   * Método que se ejecuta cuando el componente ha cambiado
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.presenter.onChanges?.(changes);
  }

  /**
   * Método que se ejecuta para validar la instancia
   * @returns boolean
   */
  validateInstance(): boolean | undefined {
    return this.presenter.validateInstance?.();
  }

  /**
   * Método que redirige a una ruta específica
   * @param route - Ruta a la que se desea redirigir
   * @returns void
   */
  redirectTo(route: string): void {
    this.router.navigate([route]);
  }
}
